import React from 'react';
import { C, font, serif, btnStyle, btnHoverEnter, btnHoverLeave } from '../shared/constants';
import { useScrollReveal, revealStyle } from '../shared';
import { ParticlesCanvas } from '../landing';
import { AnimatedCounter } from '../../lib/ImmersiveStyles';

/* ─── Gamer-style colours (only for the AppPreview, not the page) ─── */
const G = {
  bg: '#070A12',
  surface: '#0A0F1B',
  border: '#1E2740',
  grid: 'rgba(93,193,185,0.05)',
  label: '#8C97B0',
  text: '#D6DCE8',
  teal: '#5dc1b9',
  mango: '#FDBE02',
};

/* ─── Tree view data ─── */
const ADMIN_WORLDS = new Set([1, 2, 4, 5, 6, 7]); // mundos con misiones de Administración

const NODES = [
  { id: 0, label: 'Raíz',              x: 201, y: 466, status: 'done',   prog: '4/6' },
  { id: 1, label: 'Arrancar',           x: 168, y: 416, status: 'active', prog: '3/6' },
  { id: 2, label: 'Cimientos',          x: 215, y: 365, status: 'locked', prog: '0/3' },
  { id: 3, label: 'Por qué te compran', x: 174, y: 314, status: 'locked', prog: '0/4' },
  { id: 4, label: 'Sobrevivir',         x: 215, y: 263, status: 'locked', prog: '0/6' },
  { id: 5, label: 'Ordenar',            x: 174, y: 212, status: 'locked', prog: '0/10' },
  { id: 6, label: 'Eficiencia',         x: 215, y: 161, status: 'locked', prog: '0/7' },
  { id: 7, label: 'Listo para fondeo',  x: 174, y: 110, status: 'cf',     prog: '0/6' },
  { id: 8, label: 'Despegue',           x: 215, y: 59,  status: 'locked', prog: '0/4' },
  { id: 9, label: 'Madurez',            x: 194, y: 8,   status: 'locked', prog: '0/3' },
];

const ERAS = [
  { label: 'ERA 5 · MADUREZ',       top: 0,   h: 54  },
  { label: 'ERA 4 · DESPEGUE',      top: 54,  h: 102 },
  { label: 'ERA 3 · ÉXITO',         top: 156, h: 102 },
  { label: 'ERA 2 · SUPERVIVENCIA', top: 258, h: 52  },
  { label: 'ERA 1 · EXISTENCIA',    top: 310, h: 250 },
];

const MISSIONS = [
  { name: 'Tu producto estrella',      sub: '★ Cinturón piteado · $1,200',              ev: 'Tu carta',     done: true  },
  { name: 'Precio definido',           sub: 'Margen 42% por pieza',                     ev: 'Paper Trail',  done: true,  admin: true },
  { name: 'Identidad visual',          sub: 'Logo · 3 colores · Artesanal',             ev: 'Tu carta',     done: true  },
  { name: 'Redes sociales',            sub: 'Instagram · WhatsApp · Maps',              ev: 'Tu carta',     done: false },
  { name: 'Inventario de lo que tienes',sub: 'Equipo, herramientas, mercancía y dinero', ev: 'Paper Trail',  done: false, admin: true },
  { name: 'Primeras ventas',           sub: 'Ventas reales registradas.',                ev: 'Proof of Work',done: false },
];

/* ─── Inline styles for tree nodes ─── */
const nodeBase = {
  position: 'absolute', width: 40, height: 40, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 12, fontWeight: 800, fontFamily: font,
  background: '#111726', border: '2px solid #2A3350', color: '#6B7590',
};
const nodeStyles = {
  done:   { ...nodeBase, background: G.teal, borderColor: G.teal, color: '#06201D' },
  active: { ...nodeBase, background: G.bg, border: `3px solid ${G.teal}`, color: G.teal },
  locked: nodeBase,
  cf:     { ...nodeBase, borderColor: G.mango, color: G.mango },
};

const FIXED_STATS = { users: 13, projects: 10 };
const TREE_BREAKPOINT = 1100;

export default function HeroArbol({ isMobile, isTablet, scrollY = 0, onStart }) {
  const liveStats = FIXED_STATS;
  const [showTree, setShowTree] = React.useState(() => typeof window !== 'undefined' && window.innerWidth >= TREE_BREAKPOINT);

  React.useEffect(() => {
    const onResize = () => setShowTree(window.innerWidth >= TREE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const heroReveal = useScrollReveal(0.1);

  const mob = isMobile;
  const grad = `linear-gradient(90deg, ${C.turquesa} 0%, ${C.mango} 55%, ${C.coral} 100%)`;

  return (
    <section
      ref={heroReveal.ref}
      style={{
        maxWidth: 1200, margin: '0 auto',
        padding: mob ? '3rem 1rem 2rem' : '5rem 1.5rem 3rem',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Particles background */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        transform: mob ? 'none' : `translateY(${scrollY * 0.3}px)`, willChange: 'transform',
      }}>
        <ParticlesCanvas />
      </div>

      {/* Eyebrow */}
      <p style={{
        fontSize: 12, letterSpacing: 3, fontWeight: 700, textTransform: 'uppercase',
        color: C.turquesa, margin: '0 0 14px', position: 'relative', zIndex: 1,
        opacity: heroReveal.inView ? 1 : 0,
        transition: 'opacity 0.6s ease-out 0.1s',
      }}>
        TU NEGOCIO, MISIÓN POR MISIÓN
      </p>

      {/* Headline */}
      <h1 style={{
        fontFamily: serif, fontSize: mob ? 32 : isTablet ? 52 : 72,
        fontWeight: 400, lineHeight: 0.98, margin: '0 auto 20px', maxWidth: 980,
        position: 'relative', zIndex: 1,
        opacity: heroReveal.inView ? 1 : 0,
        transform: heroReveal.inView ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
      }}>
        <span style={{ color: '#fff' }}>Stats reales. </span>
        <em style={{
          background: grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          backgroundClip: 'text', fontStyle: 'italic',
        }}>
          Negocio real.
        </em>
      </h1>

      {/* Lead */}
      <p style={{
        fontSize: mob ? 15 : 19, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6,
        maxWidth: 720, margin: '0 auto 28px', position: 'relative', zIndex: 1,
        opacity: heroReveal.inView ? 1 : 0,
        transition: 'opacity 0.8s ease-out 0.5s',
      }}>
        Tu negocio es otra persona: aprende a hacerla crecer con un camino claro.
      </p>

      {/* CTAs */}
      <div style={{
        display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap',
        position: 'relative', zIndex: 1,
        flexDirection: mob ? 'column' : 'row', alignItems: 'center',
        opacity: heroReveal.inView ? 1 : 0,
        transform: heroReveal.inView ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.6s ease-out 0.8s, transform 0.6s ease-out 0.8s',
      }}>
        <button
          onClick={onStart}
          onMouseEnter={btnHoverEnter} onMouseLeave={btnHoverLeave}
          className="td-pulse-btn td-iri-btn"
          style={{ ...btnStyle, padding: mob ? '16px 32px' : '17px 44px', fontSize: mob ? 15 : 17, width: mob ? '100%' : 'auto' }}
        >
          Empieza gratis
        </button>
        <a
          href="#arbol"
          onMouseEnter={btnHoverEnter} onMouseLeave={btnHoverLeave}
          style={{
            ...btnStyle, padding: mob ? '16px 32px' : '17px 44px', fontSize: mob ? 15 : 17,
            textDecoration: 'none', display: 'inline-block', width: mob ? '100%' : 'auto', textAlign: 'center',
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: 'none',
          }}
        >
          Ver cómo funciona
        </a>
      </div>

      {/* Social proof counters */}
      <div style={{
        marginTop: 18, display: 'flex', gap: 24, justifyContent: 'center',
        fontSize: 13, color: 'rgba(255,255,255,0.4)', flexWrap: 'wrap',
        position: 'relative', zIndex: 1,
        opacity: heroReveal.inView ? 1 : 0,
        transition: 'opacity 0.8s ease-out 1s',
      }}>
        <span><strong style={{ color: '#fff' }}>
          <AnimatedCounter end={liveStats.users} inView={heroReveal.inView} duration={2000} />
        </strong> emprendedores</span>
        <span><strong style={{ color: '#fff' }}>
          <AnimatedCounter end={liveStats.projects} inView={heroReveal.inView} duration={2000} />
        </strong> proyectos activos</span>
        <span>Gratis para empezar · Sin tarjeta</span>
      </div>

      {/* ═══ APP PREVIEW (gamer-style tree) ═══ */}
      <div style={{
        margin: '44px auto 0', maxWidth: 1180,
        border: `1px solid ${G.border}`, borderRadius: 18,
        background: G.bg, overflow: 'hidden', textAlign: 'left',
        fontFamily: font, boxShadow: `0 30px 80px rgba(93,193,185,0.12)`,
        position: 'relative', zIndex: 1,
        opacity: heroReveal.inView ? 1 : 0,
        transform: heroReveal.inView ? 'translateY(0)' : 'translateY(40px)',
        transition: 'opacity 0.8s ease-out 1.2s, transform 0.8s ease-out 1.2s',
      }}>
        {/* HUD bar */}
        <div style={{
          height: 56, padding: '0 18px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: `1px solid ${G.border}`,
          background: G.surface, fontSize: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, border: `2px solid ${G.teal}`, color: G.teal,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 12, background: '#1A2236',
            }}>GA</div>
            <div>
              <strong style={{ display: 'block' }}>Talabartería GA</strong>
              <span style={{ fontSize: 9, letterSpacing: 1.5, color: G.label }}>ARTESANÍA · MUNDO ACTUAL 01</span>
            </div>
          </div>
          {!mob && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {[
                { l: 'LVL', v: '01' },
                { l: '◎', v: '12', s: 'PINES' },
                { l: '⬡', v: '01', s: 'INSIGNIAS' },
              ].map((s, i) => (
                <span key={i} style={{
                  fontSize: 10, letterSpacing: 1, color: G.label,
                  padding: '6px 10px', background: '#111726', border: `1px solid ${G.border}`,
                }}>
                  {s.l} <strong style={{ color: G.teal, fontSize: 13 }}>{s.v}</strong>{s.s ? ` ${s.s}` : ''}
                </span>
              ))}
              <span style={{
                fontSize: 10, letterSpacing: 1, color: G.label,
                padding: '6px 10px', background: '#111726', border: `1px solid ${G.border}`,
              }}>
                SALUD ADMIN. <strong style={{ color: G.mango, fontSize: 13 }}>62</strong>
              </span>
              <span style={{
                fontSize: 11, fontWeight: 800, letterSpacing: 1, padding: '8px 12px',
                background: G.mango, color: '#06201D',
              }}>MI CARTA</span>
            </div>
          )}
        </div>

        {/* Body: tree + pane */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: showTree ? '460px 1fr' : '1fr',
          minHeight: showTree ? 560 : 'auto',
        }}>
          {/* Tree view — wide screens only (≥1100px) */}
          {showTree && (
            <div style={{
              position: 'relative', height: 560, borderRight: `1px solid ${G.border}`,
              backgroundImage: `linear-gradient(${G.grid} 1px, transparent 1px), linear-gradient(90deg, ${G.grid} 1px, transparent 1px)`,
              backgroundSize: '28px 28px', overflow: 'hidden',
            }}>
              {/* Era bands */}
              {ERAS.map((era, i) => (
                <div key={i} style={{
                  position: 'absolute', left: 0, right: 0, top: era.top, height: era.h,
                  borderTop: '1px dashed #1C2436',
                  background: i % 2 === 0 ? 'rgba(12,18,34,0.6)' : 'transparent',
                }}>
                  <span style={{
                    position: 'absolute', right: 10, bottom: 6, fontSize: 8,
                    letterSpacing: 1.5, color: G.label, fontWeight: 700,
                  }}>{era.label}</span>
                </div>
              ))}

              {/* Connection lines (SVG) */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
                {NODES.slice(0, -1).map((n, i) => {
                  const next = NODES[i + 1];
                  const isDone = n.status === 'done' && next.status === 'active';
                  const isCf = next.status === 'cf';
                  return (
                    <line key={i}
                      x1={n.x + 20} y1={n.y + 20}
                      x2={next.x + 20} y2={next.y + 20}
                      stroke={isDone ? G.teal : isCf ? G.mango : '#2A3350'}
                      strokeWidth={2}
                      strokeDasharray={isDone ? 'none' : '6 4'}
                    />
                  );
                })}
              </svg>

              {/* Nodes + labels */}
              {NODES.map(n => {
                const lblLeft = n.x < 200 ? n.x - 120 : n.x - 120;
                return (
                  <React.Fragment key={n.id}>
                    <div style={{
                      position: 'absolute', width: 110, textAlign: 'right', lineHeight: 1.1,
                      left: n.x - 130, top: n.y + 4,
                    }}>
                      <span style={{ display: 'block', fontSize: 8, letterSpacing: 1, color: G.label, fontWeight: 700 }}>
                        MUNDO {String(n.id).padStart(2, '0')} · {n.prog}
                      </span>
                      <strong style={{ fontSize: 11, color: G.text }}>{n.label}</strong>
                      {ADMIN_WORLDS.has(n.id) && (
                        <span style={{ display: 'inline-block', fontSize: 7, fontWeight: 800, letterSpacing: 0.5, padding: '1px 4px', marginLeft: 4, background: 'rgba(253,190,2,0.15)', color: G.mango, borderRadius: 3 }}>ADMIN</span>
                      )}
                    </div>
                    <div style={{
                      ...nodeStyles[n.status],
                      left: n.x, top: n.y,
                      ...(n.status === 'active' ? { animation: 'pulseT 1.8s ease-in-out infinite' } : {}),
                    }}>
                      {n.status === 'done' ? '✓' : String(n.id).padStart(2, '0')}
                    </div>
                  </React.Fragment>
                );
              })}

              {/* Crowdfunding label */}
              <span style={{
                position: 'absolute', left: 262, top: 118, fontSize: 8, letterSpacing: 1,
                fontWeight: 800, padding: '4px 7px', border: `1px solid ${G.mango}`,
                color: G.mango, background: '#111726',
              }}>☆ CROWDFUNDING</span>
            </div>
          )}

          {/* Right pane — mission detail */}
          <div style={{
            padding: mob ? '18px 16px' : '18px 20px',
            display: 'flex', flexDirection: 'column', gap: 10,
            background: G.surface,
          }}>
            {/* Tags */}
            <div style={{ display: 'flex', gap: 6, fontSize: 9, letterSpacing: 1, fontWeight: 700, flexWrap: 'wrap' }}>
              <span style={{ padding: '3px 7px', background: '#1A2236', color: G.teal }}>MUNDO 01</span>
              <span style={{ color: G.label, padding: '3px 0' }}>ERA · EXISTENCIA</span>
              <span style={{ padding: '3px 7px', background: G.teal, color: '#06201D' }}>EN CURSO</span>
            </div>

            <h3 style={{ margin: 0, fontSize: mob ? 22 : 26, fontWeight: 800, lineHeight: 1, color: '#fff' }}>
              Arrancar con lo que tienes
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: '#B5BFD3' }}>
              Registras lo que ya tienes: producto, precio, tu marca y tus redes. Y vendes.
            </p>

            {/* OKR panel */}
            <div style={{
              display: 'flex', gap: 12, padding: 12, background: '#0F1A24',
              clipPath: 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
            }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: G.text }}>
                <span style={{ fontSize: 8, letterSpacing: 1.2, color: G.teal, fontWeight: 700 }}>
                  MILESTONE · EL JEFE DEL MUNDO · SE MIDE CON OKR
                </span>
                <strong style={{ fontSize: 13 }}>Objetivo: Vender con lo que ya tienes</strong>
                <span style={{ fontSize: 7, letterSpacing: 1, color: G.label }}>
                  RESULTADOS CLAVE · LOS NÚMEROS QUE PRUEBAN QUE LLEGASTE
                </span>
                <span>○ KR1 · 10 ventas registradas</span>
                <span>○ KR2 · Tu marca y tus redes en tu carta</span>
                <span>○ KR3 · Inventario inicial registrado</span>
              </div>
              {!mob && (
                <div style={{ width: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textAlign: 'center' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={G.mango} strokeWidth="1.4">
                    <polygon points="12,2 21,7 21,17 12,22 3,17 3,7" />
                  </svg>
                  <strong style={{ fontSize: 11, color: G.mango }}>Cha-Ching!</strong>
                  <div style={{ width: 100, height: 5, background: '#1A2236', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: '50%', height: '100%', background: G.teal, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 8, color: G.label, letterSpacing: 1 }}>3 / 6 MISIONES</span>
                </div>
              )}
            </div>

            {/* Missions list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {MISSIONS.map((m, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: mob ? 6 : 10, padding: mob ? '6px 8px' : '8px 10px',
                  background: m.admin ? 'rgba(253,190,2,0.06)' : '#111726',
                  border: `1px solid ${m.admin ? 'rgba(253,190,2,0.15)' : G.border}`,
                  flexWrap: 'wrap',
                }}>
                  <div style={{
                    width: 16, height: 16, border: `2px solid ${m.done ? G.teal : '#3A4560'}`,
                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: '#06201D',
                    ...(m.done ? { background: G.teal } : {}),
                  }}>
                    {m.done && '✓'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: mob ? 11 : 12, display: 'block', color: '#fff' }}>{m.name}</strong>
                    {!mob && <span style={{ fontSize: 10, color: G.label, display: 'block' }}>{m.sub}</span>}
                  </div>
                  <span style={{
                    fontSize: 8, letterSpacing: 0.5, padding: '3px 5px',
                    border: `1px solid ${G.mango}`, color: G.mango,
                  }}>{m.ev}</span>
                  {m.admin && (
                    <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: 0.5, padding: '2px 5px', background: 'rgba(253,190,2,0.15)', color: G.mango, borderRadius: 3 }}>ADMIN</span>
                  )}
                  {m.done ? (
                    <span style={{ fontSize: 9, letterSpacing: 1, color: G.teal, fontWeight: 700 }}>VALIDADA</span>
                  ) : (
                    mob ? (
                      <span style={{ fontSize: 9, color: '#3A4560' }}>pendiente</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 5 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '7px 8px', border: `1px solid ${G.teal}`, color: G.teal }}>Ya lo tengo</span>
                        <span style={{ fontSize: 10, fontWeight: 800, padding: '7px 8px', background: G.teal, color: '#06201D' }}>Hacer misión</span>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>

            {/* Stack upgrade */}
            <div style={{
              marginTop: 'auto', fontSize: 11, color: G.text,
              padding: '8px 10px', border: `1px dashed ${G.teal}`,
            }}>
              <span style={{ fontSize: 8, letterSpacing: 1.5, color: G.teal, fontWeight: 700, marginRight: 6 }}>STACK UPGRADE</span>
              WhatsApp Business · Links de pago · Canva · Libreta de ingresos y gastos
            </div>
          </div>
        </div>
      </div>

      {/* Pulse animation for tree node */}
      <style>{`
        @keyframes pulseT {
          0%, 100% { box-shadow: 0 0 0 4px rgba(93,193,185,0.18), 0 0 14px rgba(93,193,185,0.35); }
          50% { box-shadow: 0 0 0 9px rgba(93,193,185,0.06), 0 0 28px rgba(93,193,185,0.6); }
        }
      `}</style>
    </section>
  );
}
